import Hls from 'hls.js';
import { holdClamp } from './view.js';

/**
 * Public output player, held a fixed distance behind the live edge.
 *
 * HLS rather than WebRTC on purpose. WebRTC is built to minimise latency, which
 * is exactly the wrong property here: the delay IS the safety mechanism. HLS
 * keeps a segment buffer, so a viewer can sit at a chosen offset from the live
 * edge with no transcoding anywhere and no extra server cost.
 *
 * The director keeps the WebRTC path, so a human sees reality now while the
 * audience sees the version the gates have already ruled on.
 */

export interface DelayedPlayer {
  /** Seconds currently behind the live edge, or null when not yet known. */
  latency(): number | null;
  destroy(): void;
}

/**
 * hls.js drifts back toward the live edge whenever it can, so holding position
 * needs both its own target AND a watchdog. Left alone it will happily catch
 * up and quietly erase the delay, which would remove the protection without
 * removing any of the appearance of it.
 *
 * The person holding the scrubber is the third door to the same room: the
 * page's own `controls` let a viewer drag to the live edge, and a 2s timer
 * answers that up to 2s late. The `seeking` listener answers it at the seek,
 * through the same `holdClamp` the timer uses, so the two cannot disagree
 * about where the line is.
 */
function watchdog(video: HTMLVideoElement, holdSeconds: number): () => void {
  const snapBack = (): void => {
    const { seekable } = video;
    if (seekable.length === 0) return;
    const edge = seekable.end(seekable.length - 1);
    video.currentTime = holdClamp(edge - holdSeconds, edge, seekable.start(0), holdSeconds);
  };
  const timer = window.setInterval(() => {
    const { seekable } = video;
    if (seekable.length === 0) return;
    const edge = seekable.end(seekable.length - 1);
    const behind = edge - video.currentTime;

    // Drifted forward: we are closer to live than the hold allows. Seek back.
    if (behind < holdSeconds * 0.75) {
      snapBack();
      return;
    }
    // Drifted so far back we risk falling off the end of the segment window.
    if (behind > holdSeconds * 2.5) {
      snapBack();
    }
  }, 2000);
  const onSeeking = (): void => {
    const { seekable } = video;
    if (seekable.length === 0) return;
    const edge = seekable.end(seekable.length - 1);
    const target = holdClamp(video.currentTime, edge, seekable.start(0), holdSeconds);
    // Only a seek TOWARD live is corrected; a backward one is not fought over.
    // The snap lands exactly on the limit, so the seeking event this assignment
    // fires sees nothing above the line and stops: no loop.
    if (video.currentTime > target) video.currentTime = target;
  };
  video.addEventListener('seeking', onSeeking);
  return () => {
    window.clearInterval(timer);
    video.removeEventListener('seeking', onSeeking);
  };
}

export function playDelayed(
  url: string,
  video: HTMLVideoElement,
  holdSeconds: number,
  /**
   * Called whenever this player learns something about whether media is
   * actually arriving. Wave 5, D1.
   *
   * Before this the only hls.js event handled was `MANIFEST_PARSED`, both
   * `play()` calls ended `.catch(() => undefined)`, and nothing anywhere asked
   * the element whether a frame had been decoded. A total media failure was
   * therefore indistinguishable, to the page, from a healthy broadcast.
   */
  onHealth: (h: { hasFrames: boolean; fatal: boolean }) => void = () => undefined,
): DelayedPlayer {
  let stopWatchdog: (() => void) | null = null;

  /**
   * A REAL frame, not a resolved `play()` promise. `play()` resolving means
   * playback was permitted; it says nothing about bytes arriving, which is
   * exactly the confusion that let the page claim to be live over black.
   */
  const onPlaying = (): void => {
    if (video.videoWidth > 0 && video.readyState >= 2) onHealth({ hasFrames: true, fatal: false });
  };
  video.addEventListener('playing', onPlaying);
  video.addEventListener('loadeddata', onPlaying);
  // The element's own error path, which is the only one Safari's native HLS has.
  const onElementError = (): void => onHealth({ hasFrames: false, fatal: true });
  video.addEventListener('error', onElementError);

  const latency = (): number | null => {
    const { seekable } = video;
    if (seekable.length === 0) return null;
    return seekable.end(seekable.length - 1) - video.currentTime;
  };

  if (Hls.isSupported()) {
    const hls = new Hls({
      // Low latency mode would fight the entire point of this player.
      lowLatencyMode: false,
      liveSyncDuration: holdSeconds,
      liveMaxLatencyDuration: holdSeconds + 15,
      backBufferLength: 60,
    });
    hls.loadSource(url);
    hls.attachMedia(video);
    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      void video.play().catch(() => undefined);
      stopWatchdog = watchdog(video, holdSeconds);
    });
    /**
     * The handler that did not exist. hls.js reports a fatal error and then
     * stops, so without this the page waits forever for a manifest that will
     * never parse while telling the viewer it is watching a broadcast.
     *
     * Non-fatal errors are NOT reported as fatal: hls.js recovers from most of
     * them on its own, and treating a recovered hiccup as a blackout would
     * replace one dishonest state with a different one.
     */
    hls.on(Hls.Events.ERROR, (_e, data) => {
      if (data.fatal) onHealth({ hasFrames: false, fatal: true });
    });
    return {
      latency,
      destroy: () => {
        stopWatchdog?.();
        video.removeEventListener('playing', onPlaying);
        video.removeEventListener('loadeddata', onPlaying);
        video.removeEventListener('error', onElementError);
        hls.destroy();
      },
    };
  }

  // Safari plays HLS natively and ignores hls.js entirely, so the hold has to
  // be enforced purely by seeking. Less precise, and worth knowing when reading
  // a field test taken on an iPad.
  video.src = url;
  const onLoaded = (): void => {
    const { seekable } = video;
    if (seekable.length > 0) {
      video.currentTime = Math.max(
        seekable.start(0),
        seekable.end(seekable.length - 1) - holdSeconds,
      );
    }
    void video.play().catch(() => undefined);
    stopWatchdog = watchdog(video, holdSeconds);
  };
  video.addEventListener('loadedmetadata', onLoaded, { once: true });

  return {
    latency,
    destroy: () => {
      stopWatchdog?.();
      video.removeEventListener('loadedmetadata', onLoaded);
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('loadeddata', onPlaying);
      video.removeEventListener('error', onElementError);
      video.removeAttribute('src');
      video.load();
    },
  };
}

export function hlsUrl(base: string, path: string): string {
  return `${base.replace(/\/$/, '')}/${path}/index.m3u8`;
}
